import React, { useState, useEffect } from 'react';
import { IconButton, InputAdornment, CircularProgress } from '@mui/material';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { useOptionsService } from '#service/autocomplete-service/hooks';
import { SearchDeviceOptionService } from '#service/autocomplete-service/service';
import { searchToFormDevice, getExcludeIds } from './helper';
import SearchResult from './search-result';
import {
  SearchContainer,
  StyledInput,
  SearchIcon,
  Preloader
} from './style';
import { useDebounce } from '#hooks';

const DeviceSearch = ({
  handleDeviceSetData,
  formData = [],
  allFormDevices,
  disabled,
  alertId,
  projectId
}) => {
  const [value, setValue] = useState('');
  const [resultVisible, setResultVisible] = useState(false);
  const { options, status, ...serviceMethods } =
  useOptionsService(['title'], SearchDeviceOptionService);
  const loading = ['idle', 'loading'].includes(status);
  const isValue = value.length > 1;

  const debouncedFetchData = useDebounce(v => {
    const devices = allFormDevices?.length ? allFormDevices : formData;
    const excludeIds = getExcludeIds(devices);
    serviceMethods.fetchOptions(v, 500, { excludeIds, excludeProjects: projectId });
  }, 500);

  const handleChange = event => {
    const { value: newValue } = event.target;
    setValue(newValue);

    if (newValue.length > 1 && newValue !== value) {
      setResultVisible(true);
      debouncedFetchData(newValue);
    }
  };

  const handleClearButtonClick = () => {
    setValue('');
    serviceMethods.clearOptions();
  };

  const handleAddDevice = device => {
    const newDevice = searchToFormDevice(device.data, alertId);
    const newFormData = [...formData, newDevice];

    handleDeviceSetData(newFormData);
    setResultVisible(false);
    handleClearButtonClick();
  };

  useEffect(() => {
    if (value.length === 0) {
      setValue('');
      serviceMethods.clearOptions();
    }
  }, [serviceMethods, value.length]);

  return (
    <SearchContainer variant="outlined">
      <StyledInput
        endAdornment={(value) ?
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleClearButtonClick}>
              <ClearRoundedIcon />
            </IconButton>
          </InputAdornment> : null}
        startAdornment={(isValue && loading) ? (
          <Preloader>
            <CircularProgress color="inherit" size={20} />
          </Preloader>
        ) : (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        )}
        inputProps={{
          'aria-label': 'quick add'
        }}
        value={value}
        placeholder="Quick add"
        onChange={handleChange}
        disabled={disabled}
      />
      {resultVisible && <SearchResult
        isValue={isValue}
        data={options}
        loading={loading}
        handleAddDevice={handleAddDevice}
      />}
    </SearchContainer>
  );
};

export default DeviceSearch;
